import {
  CountryCode,
  Gender,
  IdentificationType,
  PartyType,
} from "Infrastructure/Api/Api";
import { getAddressValidationSchema } from "Schemas/addressSchemas";
import { partyLicenseSchema } from "Schemas/licensesSchema";
import {
  addOtherNationalitiesIssues,
  otherNationalitiesSchema,
} from "Schemas/otherNationalitiesSchema";
import { getPhoneSchema } from "Schemas/phoneSchema";
import { relatedPartySchema } from "Schemas/relatedPartySchema";
import { Resources, useResource, type TFunction } from "Translations/Resources";
import { PersonalIdentification } from "Utils/PersonalIdentification";
import { z } from "zod";

const getUserDetailValidationSchema = (t: TFunction) =>
  z
    .object({
      publicID: z.string().nullish(),
      firstName: z
        .string()
        .min(1, { message: t(Resources.Validation.Required) }),
      lastName: z
        .string()
        .min(1, { message: t(Resources.Validation.Required) }),
      companyName: z.string().nullish(),
      taxNumber: z.string().nullish(),
      companyNumber: z.string().nullish(),
      personalNumber: z
        .string()
        .min(1, { message: t(Resources.Validation.Required) })
        .max(10, {
          message: t(Resources.Validation.PersonalNumber, { count: 10 }),
        }),
      birthDate: z.string().nullish(),
      gender: z.enum(Gender),
      isForeigner: z.boolean(),
      licenses: z.array(partyLicenseSchema),
      relatedParties: z.array(relatedPartySchema),
      type: z.enum(PartyType),
      birthPlace: z
        .string()
        .min(1, { message: t(Resources.Validation.Required) }),
      nationality: z.enum(CountryCode),
      otherNationalities: otherNationalitiesSchema,
      // Form-only (never sent to the API): the "other nationality" checkbox that reveals the picker.
      hasOtherNationality: z.boolean(),
      isVATPayer: z.boolean().nullish(),
      phone: getPhoneSchema(t, { isRequired: false }),
      email: z.string().nullish(),
      partyIdentification: z
        .object({
          identification: z.enum(IdentificationType).nullable().optional(),
          identificationNumber: z.string().nullish(),
          identificationValidFrom: z.string().nullish(),
          identificationValidTo: z.string().nullish(),
          identificationIssuer: z.string().nullish(),
          isIDCardApprovedByClient: z.boolean().nullish(),
          secondIdentification: z
            .enum(IdentificationType)
            .nullable()
            .optional(),
          secondIdentificationNumber: z.string().nullish(),
          secondIdentificationValidFrom: z.string().nullish(),
          secondIdentificationValidTo: z.string().nullish(),
          secondIdentificationIssuer: z.string().nullish(),
        })
        .nullable()
        .optional(),
      companyRepresentativeFirstName: z.string().nullish(),
      companyRepresentativeLastName: z.string().nullish(),
      companyRepresentativeBirthDate: z.string().nullish(),
      address: getAddressValidationSchema(t),
      hasIdentificationsUploaded: z.boolean().nullish(),
    })
    .superRefine((obj, ctx) => {
      addOtherNationalitiesIssues({
        ctx,
        t,
        nationality: obj.nationality,
        otherNationalities: obj.otherNationalities,
      });

      if (obj.hasOtherNationality && obj.otherNationalities.length === 0) {
        ctx.addIssue({
          code: "custom",
          message: t(Resources.Validation.Required),
          path: ["otherNationalities", 0],
        });
      }

      if (obj.nationality === CountryCode.CZ) {
        const personInfo = PersonalIdentification.getInfo(
          obj.personalNumber ?? "",
        );

        if (!personInfo.IsValid) {
          ctx.addIssue({
            code: "custom",
            message: t(Resources.Validation.InvalidPersonalNumber),
            path: ["personalNumber"],
          });
        }
      }
    });

export type PersonalInformationFormData = z.infer<
  ReturnType<typeof getUserDetailValidationSchema>
>;

export type PersonalInformationFormDataInput = z.input<
  ReturnType<typeof getUserDetailValidationSchema>
>;

export const usePersonalInformationValidation = () => {
  const { t } = useResource();

  return getUserDetailValidationSchema(t);
};
